/**
 * Presenting a candidate to an employer — the words and the limits, in one
 * place (drizzle/0069).
 *
 * Imported by the collection config and by the three admin screens that show
 * a presentation, so the team reads the employer's answer the same way on the
 * company's cover, on the candidate's cover and in the raw table. The employer's
 * own wording lives in the site (`src/common/presentations.ts`); the two are
 * different sentences about the same four states, because one side presses the
 * button and the other reads what was pressed.
 *
 * Pure, so `admin-app/backend/src/services/presentations-contract.test.ts` can
 * pin it without React or a database.
 */

export type PresentationState = "new" | "seen" | "contacted" | "declined";

export const PRESENTATION_STATES: readonly PresentationState[] = ["new", "seen", "contacted", "declined"];

/**
 * What the employer did, in the team's words.
 *
 * `seen` is the one to read carefully. The dashboard sets it when it shows the
 * card — which is what we can observe — so it says they opened their panel,
 * never that anybody read the profile. Every label is a verb with no object, so
 * none of them guesses the candidate's gender.
 */
export const PRESENTATION_STATE_LABEL: Record<PresentationState, string> = {
    new: "Aún no han entrado",
    seen: "Han abierto su panel",
    contacted: "Han contactado",
    declined: "No les encaja"
};

/** The longer sentence, for a hint under the chip. */
export const PRESENTATION_STATE_HINT: Record<PresentationState, string> = {
    new: "Nadie de la empresa ha abierto su panel desde que se lo presentamos.",
    seen: "La empresa abrió su panel con este perfil ya dentro. Eso no dice si lo leyeron.",
    contacted: "La empresa marcó que ha contactado con esta persona.",
    declined: "La empresa marcó que no le encaja, y dijo por qué."
};

/** Rebase chip colours. Only a decline is a warm colour: it is the one to read. */
export const PRESENTATION_STATE_COLOR: Record<PresentationState, string> = {
    new: "grayLighter",
    seen: "blueLighter",
    contacted: "greenLighter",
    declined: "orangeLighter"
};

export function presentationState(value: unknown): PresentationState {
    return PRESENTATION_STATES.includes(value as PresentationState) ? value as PresentationState : "new";
}

/** Keep in step with the CHECK constraints in drizzle/0069. */
export const NOTE_MAX = 1500;
export const DECLINE_REASON_MAX = 280;

/**
 * Whether a recruiter's note can be saved, and if not, why — in words.
 *
 * Required, because a presentation without a reason is a name, and a name is
 * what the shortlist email already sends.
 */
export function noteProblem(note: string | null | undefined): string | null {
    const text = (note ?? "").trim();
    if (!text) return "Escribe qué le decimos a la empresa sobre esta persona.";
    if (text.length > NOTE_MAX) return `La nota pasa de ${NOTE_MAX} caracteres; recórtala.`;
    return null;
}

/**
 * The sentence a presentation reads as on the team's side.
 *
 * `when` is pre-formatted by the caller (the covers already own `sinceLabel`),
 * so this stays free of dates and locales.
 */
export function presentationAnswerLine(
    state: PresentationState,
    declineReason: string | null | undefined,
    when: string | null
): string {
    const label = PRESENTATION_STATE_LABEL[state];
    const dated = when && state !== "new" ? `${label} ${when}` : label;
    const reason = (declineReason ?? "").trim();
    return state === "declined" && reason ? `${dated}: «${reason}»` : dated;
}
